import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Trophy, Star, Sparkles, Medal, Flame } from "lucide-react";

const ICON = { flame: Flame, trophy: Trophy, star: Star, sparkles: Sparkles, medal: Medal };
const COLORS = ["#1E6B52", "#2A8555", "#C88A2B", "#D96B43", "#F2D79A"];

// Gentle, brand-coloured confetti — soft and celebratory, not overwhelming.
function gentleBurst() {
  const base = { spread: 55, startVelocity: 32, gravity: 0.9, ticks: 130, colors: COLORS, scalar: 0.9, disableForReducedMotion: true };
  confetti({ ...base, particleCount: 26, origin: { x: 0.3, y: 0.7 }, angle: 65 });
  confetti({ ...base, particleCount: 26, origin: { x: 0.7, y: 0.7 }, angle: 115 });
  setTimeout(() => confetti({ ...base, particleCount: 18, origin: { x: 0.5, y: 0.6 }, angle: 90, spread: 70 }), 220);
}

export default function BadgeCelebration({ badges, onClose }) {
  useEffect(() => {
    if (badges && badges.length) {
      gentleBurst();
      const t = setTimeout(onClose, 5200);
      return () => clearTimeout(t);
    }
  }, [badges, onClose]);

  return (
    <AnimatePresence>
      {badges && badges.length > 0 && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-center p-6"
          style={{ background: "rgba(20,30,26,0.45)", backdropFilter: "blur(3px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid="badge-celebration"
        >
          <motion.div
            initial={{ scale: 0.85, y: 18 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="bg-[var(--ns-surface)] rounded-[var(--ns-radius)] border border-[var(--ns-border)] p-7 text-center max-w-xs w-full ns-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--ns-gold)] mb-3">New trophy unlocked</div>
            <div className="grid gap-3">
              {badges.map((b, i) => {
                const Icon = ICON[b.icon] || Trophy;
                return (
                  <motion.div key={b.key} initial={{ scale: 0, rotate: -12 }} animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.15 + i * 0.12, type: "spring", stiffness: 300, damping: 14 }}
                    className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 rounded-full grid place-items-center" style={{ background: "#F2D79A", color: "#8A5E15", boxShadow: "0 6px 22px -6px rgba(200,138,43,0.6)" }}>
                      <Icon size={30} />
                    </div>
                    <div className="font-head text-lg font-bold text-[var(--ns-text)]">{b.title}</div>
                    <div className="text-sm text-[var(--ns-muted)] leading-snug">{b.description}</div>
                  </motion.div>
                );
              })}
            </div>
            <button onClick={onClose} data-testid="celebration-close" className="mt-5 text-sm font-semibold text-[var(--ns-primary)]">Nice! Keep going →</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
